import { useCallback, useMemo, useState } from 'react';
import { difference } from 'es-toolkit';

import { type QuizGenerateItem } from '@/api/ai/type';
import { adaptAiGeneratedQuizzesToBuildForm } from '@/views/build/features/ai-quiz-generation/utils';

import { MAX_QUIZ_COUNT } from '../../constant';
import { useBuildQuestionAlert } from '../../hook/useBuildQuestionAlert';
import { aiSaveAnimation } from '../../util/aiSaveAnimation';
import { useQuizControl } from '../EditableQuizCard/QuizControlProvider';
import { useQuizList } from '../useQuizList';

export const useAiGeneratedQuizItem = (generatedItems: QuizGenerateItem[]) => {
  const [selectedItems, setSelectedItems] = useState<QuizGenerateItem[]>([]);
  const [savedItems, setSavedItems] = useState<QuizGenerateItem[]>([]);

  const { handleInsert, quizList } = useQuizList();
  const { index } = useQuizControl();

  const unSavedItems = useMemo(() => {
    return difference(generatedItems, savedItems);
  }, [generatedItems, savedItems]);

  // 남은 저장 가능한 개수
  const remainingSaveQuota = useMemo(() => {
    return MAX_QUIZ_COUNT - quizList.length;
  }, [quizList.length]);

  const {
    openQuestionCountLimitDetailedAlert,
    openQuestionCountLimitAlert,
    openQuestionSaveSuccessAlert,
  } = useBuildQuestionAlert();

  const handleSelectItem = useCallback(
    (item: QuizGenerateItem) => {
      const isSelected = selectedItems.includes(item);

      if (isSelected) {
        setSelectedItems(selectedItems.filter(d => d !== item));
        return;
      }

      if (selectedItems.length >= remainingSaveQuota) {
        openQuestionCountLimitDetailedAlert(MAX_QUIZ_COUNT, remainingSaveQuota);
        return;
      }
      setSelectedItems([...selectedItems, item]);
    },
    [selectedItems, remainingSaveQuota, openQuestionCountLimitDetailedAlert],
  );

  const handleSelectItemsAtOnce = useCallback(
    (items: QuizGenerateItem[]) => {
      const notSavedItems = items.filter(item => !savedItems.includes(item));

      setSelectedItems([...notSavedItems.slice(0, remainingSaveQuota)]);
    },
    [savedItems, remainingSaveQuota],
  );

  const handleSaveItems = useCallback(
    (items: QuizGenerateItem[]) => {
      if (items.length > remainingSaveQuota) {
        openQuestionCountLimitAlert();
        return false;
      }

      const newQuizList = adaptAiGeneratedQuizzesToBuildForm(items);
      handleInsert(index + 1, newQuizList);

      openQuestionSaveSuccessAlert();

      setSavedItems([...savedItems, ...items]);
      setSelectedItems([]);

      aiSaveAnimation(newQuizList.map(quiz => quiz.id));

      if (items.length + quizList.length >= MAX_QUIZ_COUNT) return false;
      return true;
    },
    [
      savedItems,
      remainingSaveQuota,
      handleInsert,
      openQuestionSaveSuccessAlert,
      openQuestionCountLimitAlert,
      quizList,
      index,
    ],
  );

  const reset = useCallback(() => {
    setSelectedItems([]);
    setSavedItems([]);
  }, []);

  return {
    selectedItems,
    savedItems,
    unSavedItems,
    setSelectedItems,
    handleSelectItem,
    handleSelectItemsAtOnce,
    handleSaveItems,
    reset,
  };
};
